<script setup lang="ts">
import { computed, watch } from 'vue';
import {
    BDialog, BChip, BBtn, BIcon,
} from '@/design-system';
import { useDialogStore } from '@/stores/dialogStore';
import { useTranslation } from '@/composables';
import type { AntennaReadingType } from '@/types/AntennaReadingType';
import type { AntennaType } from '@/types/AntennaType';

type PropsType = {
    antennaUuid: string;
    getAntennaInfo: (uuid: string) => AntennaType | undefined;
    getAntennaReadings: (uuid: string) => AntennaReadingType[];
};

const props = defineProps<PropsType>();
const dialogId = 'antenna-current-state-modal';
const dialogStore = useDialogStore();
const { t } = useTranslation();

const isOpen = defineModel<boolean>('open', { required: true, default: false });

const antennaInfo = computed(() => props.getAntennaInfo(props.antennaUuid));
const antennaReadings = computed(() => props.getAntennaReadings(props.antennaUuid));
const lastReading = computed(() => antennaReadings.value[0]);
const currentEpcCount = computed(() => lastReading.value?.readings?.length || 0);
const lastUpdateTime = computed(() => lastReading.value?.timestamp || '');
const currentEpcs = computed(() => lastReading.value?.readings || []);
const hasData = computed(() => currentEpcs.value.length > 0);

watch(
    () => isOpen.value,
    async (newValue: boolean) => {
        if (newValue) {
            dialogStore.open(dialogId);
        } else {
            dialogStore.close(dialogId);
        }
    },
    { deep: true, immediate: true },
);

const handleClose = () => {
    isOpen.value = false;
    dialogStore.close(dialogId);
};
</script>

<template>
    <BDialog
        :id="dialogId"
        :max-width="700"
        :show-save-and-close="false"
        @cancel="handleClose"
    >
        <template #header>
            <div class="d-flex flex-column">
                <div class="d-flex align-center ga-2">
                    <h3 class="text-h6">{{ antennaInfo?.reader_name }} - {{ antennaInfo?.name }}</h3>
                </div>
                <code class="text-caption text-medium-emphasis">{{ antennaInfo?.uuid }}</code>
            </div>
        </template>

        <template #body>
            <div class="d-flex flex-column ga-4">
                <div class="d-flex ga-4">
                    <div class="d-flex flex-column">
                        <span class="text-caption text-medium-emphasis">
                            {{ t('antennas.lastUpdate') }}
                        </span>
                        <span class="text-body-2">{{ lastUpdateTime || t('common.never') }}</span>
                    </div>
                </div>

                <div
                    v-if="hasData"
                    class="d-flex flex-column ga-2"
                >
                    <span class="text-body-2 font-weight-medium">
                        {{ currentEpcCount }} {{ t('antennas.currentEpcs') }}:
                    </span>
                    <div class="d-flex flex-wrap ga-2">
                        <BChip
                            v-for="(epc, index) in currentEpcs"
                            :key="index"
                            color="grey-300"
                            size="small"
                        >
                            {{ epc }}
                        </BChip>
                    </div>
                </div>

                <div
                    v-else
                    class="d-flex flex-column align-center ga-2 py-8"
                >
                    <BIcon
                        icon="mdi-antenna-off"
                        size="48"
                        color="grey-400"
                    />
                    <span class="text-body-2 text-medium-emphasis">
                        {{ t('antennas.noCurrentReadings') }}
                    </span>
                </div>
            </div>
        </template>

        <template #actions="{ cancel }">
            <BBtn
                variant="tonal"
                color="secondary"
                rounded
                class="btn-dialog-cta-action"
                @click="cancel"
            >
                {{ t('common.close') }}
            </BBtn>
        </template>
    </BDialog>
</template>
